import { useEffect } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { Link, Prompt, Redirect, useHistory } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as Combobox from '@givebutter/primitives/combobox';
import * as Draggable from '@givebutter/primitives/draggable';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { Separator } from '@givebutter/primitives/separator';
import * as Sortable from '@givebutter/primitives/sortable';
import { useToast } from '@givebutter/primitives/toast';
import { faLinkSlash, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RuleItem } from './RuleItem';
import {
    fetchQuickBooksRules,
    saveQuickBooksSetting,
    updateQuickBooksRuleOrder,
} from 'api/quickBooks';
import * as DisconnectQuickBooksDialog from 'assets/js/pages/account-settings/account-integrations/QuickBooks/DisconnectQuickBooksDialog';
import * as QuickBooks from 'assets/js/pages/account-settings/account-integrations/QuickBooks/QuickBooks';
import { QuickBooksCustomerLookup } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/constants';
import { useQuickBooksDetails } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/useQuickBooksDetails';
import { useQuickBooksRefreshMapping } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/useQuickBooksRefreshMapping';
import { useQuickBooksStatus } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/useQuickBooksStatus';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useUnsavedChangesWarning } from 'customHooks/useUnsavedChangesWarning';
import { handleDragEnd } from 'helpers/handle-drag-end';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface QuickBooksConfigureFormValues {
    customerLookup: QuickBooksCustomerLookup;
    syncRefunds: 'yes' | 'no';
    transactionMapping: 'sales_receipts' | 'invoices';
    autoSync: boolean;
    defaultMappingServiceItem: string[];
    defaultMappingClass: string[];
    defaultMappingAccount: string[];
    ruleOrder: number[];
}

const QuickBooksConfigure = () => {
    const { accountId } = useCurrentAccount();
    const { error, success } = useToast();
    const history = useHistory();
    const { data: statusData } = useQuickBooksStatus();
    const status = statusData.status;
    const { data: qbDetails } = useQuickBooksDetails();
    const { mapping, settings } = qbDetails;
    const { isPending: isRefreshingMapping, mutate: refreshMapping } =
        useQuickBooksRefreshMapping();

    const {
        data: { rules, defaultRuleOrder },
    } = useSuspenseQuery({
        queryKey: accountQueryKeys.quickBooksRules(accountId),
        queryFn: () => fetchQuickBooksRules(accountId),
        select: rules => {
            return {
                rules,
                defaultRuleOrder: rules.map(r => r.id),
            };
        },
    });

    const methods = Form.useForm<QuickBooksConfigureFormValues>({
        defaultValues: {
            customerLookup: settings?.customer_lookup ?? QuickBooksCustomerLookup.FirstNameLastName,
            syncRefunds: settings?.sync_refunds === true ? 'yes' : 'no',
            autoSync: settings?.auto_sync ?? true,
            transactionMapping: 'sales_receipts',
            defaultMappingServiceItem: settings?.default_donation_mapping?.default_service_item
                ? [settings?.default_donation_mapping?.default_service_item.toString()]
                : [],
            defaultMappingClass: settings?.default_donation_mapping?.default_class
                ? [settings?.default_donation_mapping?.default_class]
                : [],
            defaultMappingAccount: settings?.default_donation_mapping?.default_account
                ? [settings?.default_donation_mapping?.default_account.toString()]
                : [],
            ruleOrder: defaultRuleOrder,
        },
    });

    const {
        formState: { isDirty },
        control,
        path,
        handleSubmit,
        setValue,
        watch,
    } = methods;
    const [ruleOrder, autoSync] = watch(['ruleOrder', 'autoSync']);

    const { isPending: isSaving, mutate: saveSettings } = useMutation({
        mutationFn: async (values: QuickBooksConfigureFormValues) => {
            if (ruleOrder.length > 0) {
                await updateQuickBooksRuleOrder(accountId, { order: ruleOrder });
            }

            return saveQuickBooksSetting(accountId, {
                settings: {
                    sync_refunds: values.syncRefunds === 'yes',
                    auto_sync: values.autoSync,
                    customer_lookup: values.customerLookup,
                    default_donation_mapping: {
                        default_service_item: values.defaultMappingServiceItem?.[0]
                            ? Number(values.defaultMappingServiceItem?.[0])
                            : null,
                        default_class: values.defaultMappingClass?.[0]
                            ? values.defaultMappingClass?.[0]
                            : null,
                        default_account: values.defaultMappingAccount?.[0]
                            ? Number(values.defaultMappingAccount?.[0])
                            : null,
                    },
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.quickBooks(accountId),
            });
            success('QuickBooks configuration saved!');
            history.push(`/accounts/${accountId}/settings/integrations/quickbooks`);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Unknown error');
        },
    });

    useEffect(() => {
        setValue('ruleOrder', defaultRuleOrder);
    }, [defaultRuleOrder, setValue]);

    useUnsavedChangesWarning(isDirty);

    if (!status) {
        return <Redirect to={`/accounts/${accountId}/settings/integrations/quickbooks`} />;
    }

    return (
        <Form.Provider {...methods}>
            <Prompt
                when={isDirty && !isSaving}
                message={location => {
                    if (
                        location.pathname.startsWith(
                            `/accounts/${accountId}/settings/integrations/quickbooks/configure`,
                        )
                    ) {
                        return true;
                    }

                    return 'You have unsaved changes to your QuickBooks configuration. Are you sure you want to leave?';
                }}
            />
            <form
                onSubmit={handleSubmit(
                    saveSettings as SubmitHandler<QuickBooksConfigureFormValues>,
                )}
            >
                <QuickBooks.Root>
                    <QuickBooks.Header>
                        <QuickBooks.Logo />
                        <div className="flex gap-4">
                            <Button
                                variant="outline"
                                color="neutral"
                                size="sm"
                                onClick={() =>
                                    history.push(
                                        `/accounts/${accountId}/settings/integrations/quickbooks`,
                                    )
                                }
                            >
                                Close
                            </Button>
                            <Form.SubmitButton size="sm" disabled={isSaving}>
                                Save
                            </Form.SubmitButton>
                        </div>
                    </QuickBooks.Header>
                    <Box className="p-8">
                        <div className="text-xl font-medium">Configure</div>
                        <div className="flex flex-col gap-8">
                            <Form.Container className="flex flex-col gap-2">
                                <Form.Label name={path('transactionMapping')}>
                                    How do you want Transactions mapped into QuickBooks?
                                </Form.Label>
                                <Form.UseFormRadioGroup
                                    control={control}
                                    name={path('transactionMapping')}
                                    variant={'card'}
                                    className="flex gap-2 sm:flex-col"
                                >
                                    <RadioGroup.Item
                                        id={'sales_receipts'}
                                        value={'sales_receipts'}
                                        className="flex items-center gap-3"
                                    >
                                        <RadioGroup.IndicatorContainer className="mt-0">
                                            <RadioGroup.Indicator />
                                        </RadioGroup.IndicatorContainer>
                                        <Label htmlFor={'sales_receipts'}>
                                            All transactions will be entered as sales receipts
                                        </Label>
                                    </RadioGroup.Item>
                                </Form.UseFormRadioGroup>
                            </Form.Container>

                            <Form.Container className="flex flex-col gap-2">
                                <Form.Label name={path('syncRefunds')}>
                                    Do you want Refunds to sync to QuickBooks?
                                </Form.Label>
                                <Form.UseFormRadioGroup
                                    control={control}
                                    name={path('syncRefunds')}
                                    variant={'card'}
                                    className="flex gap-2 sm:flex-col"
                                >
                                    <RadioGroup.Item
                                        id={'yes_sync_refunds'}
                                        value={'yes'}
                                        className="flex items-center gap-3"
                                    >
                                        <RadioGroup.IndicatorContainer className="mt-0">
                                            <RadioGroup.Indicator />
                                        </RadioGroup.IndicatorContainer>
                                        <Label htmlFor={'yes_sync_refunds'}>
                                            Yes, these will sync with QuickBooks
                                        </Label>
                                    </RadioGroup.Item>
                                    <RadioGroup.Item
                                        id={'no_sync_refunds'}
                                        value={'no'}
                                        className="flex items-center gap-3"
                                    >
                                        <RadioGroup.IndicatorContainer className="mt-0">
                                            <RadioGroup.Indicator />
                                        </RadioGroup.IndicatorContainer>
                                        <Label htmlFor={'no_sync_refunds'}>
                                            No, these will be manually entered
                                        </Label>
                                    </RadioGroup.Item>
                                </Form.UseFormRadioGroup>
                            </Form.Container>

                            <Form.Container className="flex flex-col gap-2">
                                <Form.Label name={path('customerLookup')}>
                                    How are your Customer display names formatted in QuickBooks?
                                </Form.Label>
                                <Form.UseFormRadioGroup
                                    control={control}
                                    name={path('customerLookup')}
                                    variant={'card'}
                                    className="flex gap-2 sm:flex-col"
                                >
                                    <RadioGroup.Item
                                        id={'first-last-name'}
                                        value={QuickBooksCustomerLookup.FirstNameLastName}
                                        className="flex items-center gap-3"
                                    >
                                        <RadioGroup.IndicatorContainer className="mt-0">
                                            <RadioGroup.Indicator />
                                        </RadioGroup.IndicatorContainer>
                                        <Label htmlFor={'first-last-name'}>
                                            FirstName LastName
                                        </Label>
                                    </RadioGroup.Item>
                                    <RadioGroup.Item
                                        id={'last-first-name'}
                                        value={QuickBooksCustomerLookup.LastNameFirstName}
                                        className="flex items-center gap-3"
                                    >
                                        <RadioGroup.IndicatorContainer className="mt-0">
                                            <RadioGroup.Indicator />
                                        </RadioGroup.IndicatorContainer>
                                        <Label htmlFor={'last-first-name'}>
                                            LastName FirstName
                                        </Label>
                                    </RadioGroup.Item>
                                    <RadioGroup.Item
                                        id={'last-comma-first-name'}
                                        value={QuickBooksCustomerLookup.LastNameCommaFirstName}
                                        className="flex items-center gap-3"
                                    >
                                        <RadioGroup.IndicatorContainer className="mt-0">
                                            <RadioGroup.Indicator />
                                        </RadioGroup.IndicatorContainer>
                                        <Label htmlFor={'last-comma-first-name'}>
                                            LastName, FirstName
                                        </Label>
                                    </RadioGroup.Item>
                                </Form.UseFormRadioGroup>
                            </Form.Container>

                            <div className="flex flex-col gap-3">
                                <Label>Enable daily syncing?</Label>
                                <Form.Container className="flex flex-row items-center gap-3">
                                    <Form.Switch name={path('autoSync')} control={control} />
                                    <div>
                                        <Form.Label name={path('autoSync')}>
                                            Automatic daily syncing
                                        </Form.Label>
                                        <p className="text-sm text-gray-600">
                                            {`We ${autoSync ? 'will' : "won't"} automatically sync to your connected QuickBooks`}
                                        </p>
                                    </div>
                                </Form.Container>
                                <p className="text-sm text-gray-600">
                                    {autoSync
                                        ? "We'll push daily transaction updates to your QuickBooks"
                                        : "You'll need to manually push updates to your QuickBooks from Givebutter's QuickBooks page"}
                                </p>
                            </div>

                            <Separator />

                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div className="flex flex-col gap-1">
                                    <p className="text-lg font-semibold">Create mapping rules</p>
                                    <p className="text-sm text-gray-600">
                                        All rules should be created before transactions begin
                                        syncing
                                    </p>
                                </div>
                                <Link
                                    to={`/accounts/${accountId}/settings/integrations/quickbooks/configure/rules/new`}
                                >
                                    <Button variant="outline" color="neutral" size="sm">
                                        <FontAwesomeIcon icon={faPlus} />
                                        Create custom rule
                                    </Button>
                                </Link>
                            </div>

                            {rules.length === 0 && (
                                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                    <p className="text-sm font-medium text-gray-500">
                                        Create your first custom rule
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Service item | Class | Account
                                    </p>
                                </div>
                            )}

                            {rules.length > 0 && (
                                <div className="flex flex-col gap-3">
                                    <Draggable.Root
                                        onDragEnd={event => {
                                            setValue('ruleOrder', handleDragEnd(event, ruleOrder), {
                                                shouldDirty: true,
                                            });
                                        }}
                                    >
                                        <Sortable.Root items={ruleOrder}>
                                            {ruleOrder.map(ruleId => {
                                                const rule = rules.find(rule => rule.id === ruleId);

                                                if (!rule) {
                                                    return null;
                                                }

                                                return (
                                                    <Sortable.Item key={rule.id} itemId={rule.id}>
                                                        <RuleItem rule={rule} />
                                                    </Sortable.Item>
                                                );
                                            })}
                                        </Sortable.Root>
                                    </Draggable.Root>
                                </div>
                            )}

                            <Separator />

                            <div className="flex flex-col gap-3">
                                <div>
                                    <Label className="text-base">Default mapping *</Label>
                                    <p className="text-sm text-gray-600">
                                        Givebutter will sync to QuickBooks according to this mapping
                                    </p>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <Form.Container>
                                        <Form.Label name={path('defaultMappingServiceItem')}>
                                            Service item
                                        </Form.Label>

                                        <Form.Combobox
                                            resource="service item"
                                            control={control}
                                            name={path('defaultMappingServiceItem')}
                                            allowMultiple={false}
                                        >
                                            <Combobox.Trigger aria-label="Select service item">
                                                <Combobox.Select placeholder="Select service item" />
                                            </Combobox.Trigger>
                                            <Combobox.Popover>
                                                <Combobox.Items>
                                                    {mapping?.data.item_service?.map(
                                                        ({ id, name }) => (
                                                            <Combobox.Item value={id} key={id}>
                                                                <Combobox.ItemText>
                                                                    {name}
                                                                </Combobox.ItemText>
                                                                <Combobox.CheckmarkIndicator />
                                                            </Combobox.Item>
                                                        ),
                                                    )}
                                                    {mapping?.data.item_service?.length === 0 && (
                                                        <Combobox.NoneFoundText>
                                                            No service items found
                                                        </Combobox.NoneFoundText>
                                                    )}
                                                </Combobox.Items>
                                            </Combobox.Popover>
                                        </Form.Combobox>
                                    </Form.Container>

                                    <Form.Container>
                                        <Form.Label name={path('defaultMappingClass')}>
                                            Class
                                        </Form.Label>
                                        <Form.Combobox
                                            resource="class"
                                            control={control}
                                            name={path('defaultMappingClass')}
                                            allowMultiple={false}
                                        >
                                            <Combobox.Trigger aria-label="Select class">
                                                <Combobox.Select placeholder="Select class" />
                                            </Combobox.Trigger>
                                            <Combobox.Popover>
                                                <Combobox.Items>
                                                    {mapping?.data.classes?.map(({ id, name }) => (
                                                        <Combobox.Item value={id} key={id}>
                                                            <Combobox.ItemText>
                                                                {name}
                                                            </Combobox.ItemText>
                                                            <Combobox.CheckmarkIndicator />
                                                        </Combobox.Item>
                                                    ))}
                                                    {mapping?.data.classes?.length === 0 && (
                                                        <Combobox.NoneFoundText>
                                                            No classes found
                                                        </Combobox.NoneFoundText>
                                                    )}
                                                </Combobox.Items>
                                            </Combobox.Popover>
                                        </Form.Combobox>
                                    </Form.Container>

                                    <Form.Container>
                                        <Form.Label name={path('defaultMappingAccount')}>
                                            Deposit account
                                        </Form.Label>
                                        <Form.Combobox
                                            resource="account"
                                            control={control}
                                            name={path('defaultMappingAccount')}
                                            allowMultiple={false}
                                            rules={{
                                                validate: value =>
                                                    (Array.isArray(value) && value.length > 0) ||
                                                    'Default account mapping is required',
                                            }}
                                        >
                                            <Combobox.Trigger aria-label="Select account">
                                                <Combobox.Select placeholder="Select account" />
                                            </Combobox.Trigger>
                                            <Combobox.Popover>
                                                <Combobox.Items>
                                                    {mapping?.data.accounts?.map(({ id, name }) => (
                                                        <Combobox.Item value={id} key={id}>
                                                            <Combobox.ItemText>
                                                                {name}
                                                            </Combobox.ItemText>
                                                            <Combobox.CheckmarkIndicator />
                                                        </Combobox.Item>
                                                    ))}
                                                    {mapping?.data.accounts?.length === 0 && (
                                                        <Combobox.NoneFoundText>
                                                            No accounts found
                                                        </Combobox.NoneFoundText>
                                                    )}
                                                </Combobox.Items>
                                            </Combobox.Popover>
                                        </Form.Combobox>
                                        <Form.Error name={path('defaultMappingAccount')} />
                                    </Form.Container>
                                </div>
                                <p className="flex gap-1 text-xs">
                                    Updated your QuickBooks service items, accounts, or classes
                                    recently?
                                    <Button
                                        variant="none"
                                        onClick={() => refreshMapping()}
                                        disabled={isRefreshingMapping}
                                        className="text-blue-600"
                                    >
                                        Refresh details
                                    </Button>
                                </p>
                            </div>

                            <div className="flex items-center justify-between gap-4">
                                <DisconnectQuickBooksDialog.Root>
                                    <DisconnectQuickBooksDialog.Trigger asChild>
                                        <Button
                                            variant="transparent"
                                            color="error"
                                            size="sm"
                                            className="pl-0"
                                        >
                                            <FontAwesomeIcon icon={faLinkSlash} /> Disconnect
                                            QuickBooks
                                        </Button>
                                    </DisconnectQuickBooksDialog.Trigger>
                                </DisconnectQuickBooksDialog.Root>
                            </div>
                        </div>
                    </Box>
                </QuickBooks.Root>
            </form>
        </Form.Provider>
    );
};

export { QuickBooksConfigure };
